import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LuX } from 'react-icons/lu';

const YoutubeModal = ({ isOpen, onClose, videoId }) => {
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    let target = document.getElementById('youtube-modal-root');

    if (!target) {
      target = document.createElement('div');
      target.id = 'youtube-modal-root';
      document.body.appendChild(target);
    }

    setPortalTarget(target);
  }, []);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !videoId || !portalTarget) return null;

  const iframeUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="YouTube video player"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.88 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 18 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0, y: 18 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative z-[10000] w-full max-w-6xl"
          >
            <button
              onClick={onClose}
              className="absolute -top-3 right-0 z-[10001] flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black text-white shadow-xl transition-transform hover:scale-105 sm:-top-4 sm:right-1"
            >
              <LuX className="h-6 w-6" />
            </button>
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-black shadow-2xl">
              <iframe
                className="aspect-video h-auto w-full"
                src={iframeUrl}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    portalTarget
  );
};

export default YoutubeModal; 
